import { IterUtils } from "../../../../../Common/IterableUtils";
import { arrayHasAtLeastOneItem, isNotNullOrEmpty, reject } from "../../../../../Common/TypingUtils";
import { IFormReadonlyModelAccessor } from "../../Form/IFormModelAccessor";
import { DataDeclarationHelper } from "../DataDeclaration/DataDeclarationHelper";
import { AbsoluteModelPath, createAbsolute, isModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ILazyLoadingManager } from "../LazyLoading/LazyLoadingManager";
import { BindingPath } from "../CoreTypes";
import { ICandyApiDraftClient } from "../../../../../Common/CandyApiClient/ICandyApiClient";

import { PathUtils } from "./PathUtils";

export interface IPathMapper {
    mapPath(outerPath: string): string;
}

export class NavigationPathMapper implements IPathMapper {
    private readonly dataDeclarationHelper: DataDeclarationHelper;
    private readonly modelAccessor: IFormReadonlyModelAccessor;
    private readonly lazyLoadingManager: ILazyLoadingManager;
    private readonly candyApiDraftClient: ICandyApiDraftClient;

    public constructor(
        modelAccessor: IFormReadonlyModelAccessor,
        dataDeclarationHelper: DataDeclarationHelper,
        lazyLoadingManager: ILazyLoadingManager,
        candyApiDraftClient: ICandyApiDraftClient
    ) {
        this.candyApiDraftClient = candyApiDraftClient;
        this.modelAccessor = modelAccessor;
        this.dataDeclarationHelper = dataDeclarationHelper;
        this.lazyLoadingManager = lazyLoadingManager;
    }

    public isCandyPath(path: BindingPath | AbsoluteModelPath): boolean {
        const pathStr = isModelPath(path) ? path.toLegacyPath() : path;
        return this.dataDeclarationHelper.hasMask(pathStr.startsWith("/") ? pathStr.slice(1) : pathStr);
    }

    public mapPath(outerPath: string): string {
        const resultPathParts = [];
        const tokens = this.removeLastInnerTextToken(this.splitToPathTokens(outerPath));
        const isCandyPath = this.isCandyPath(outerPath);
        const tokenStep = isCandyPath ? 1 : 2;
        for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex += tokenStep) {
            const pathPart = tokens[tokenIndex] ?? reject(`Invalid path ${outerPath}`);
            const filterOrIndex = tokens[tokenIndex + 1];

            resultPathParts.push(pathPart);

            const currentPath = PathUtils.join(resultPathParts);

            if (this.dataDeclarationHelper.isNodeHasChildrenEntry(currentPath)) {
                if (filterOrIndex != undefined && this.isInstanceIndex(filterOrIndex)) {
                    const highlanderInstanceIndex = this.parseInstanceIndex(filterOrIndex);
                    const instances = this.modelAccessor.getChildren(currentPath);
                    const isLastToken = tokenIndex === tokens.length - 1 || tokenIndex === tokens.length - 2;
                    const mappedInstance = isCandyPath
                        ? instances?.[highlanderInstanceIndex]
                        : instances?.[highlanderInstanceIndex - 1];
                    if (mappedInstance != undefined) {
                        resultPathParts.push(mappedInstance);
                        if (isCandyPath) {
                            tokenIndex += 1;
                        }
                    } else if (!isLastToken && !isCandyPath) {
                        resultPathParts.push((highlanderInstanceIndex - 1).toString());
                    }
                } else if (filterOrIndex != undefined) {
                    const [instanceFilters, indexAfterFilter] = this.extractFilters(filterOrIndex);
                    const candyInstanceIndex = this.findIndexMatchTo(currentPath, instanceFilters, indexAfterFilter);
                    resultPathParts.push(candyInstanceIndex).toString();
                }
            }
        }
        return resultPathParts.join("/");
    }

    public async mapPathWithLazyLoading(outerPath: string): Promise<string> {
        const tokens = this.removeLastInnerTextToken(this.splitToPathTokens(outerPath));
        const correctedPath = tokens.join("/");
        const isCandyPath = this.isCandyPath(outerPath);
        const mappedPath = isCandyPath
            ? correctedPath
            : (await this.candyApiDraftClient.resolveInnerPath({ errorLogPath: correctedPath })).innerPath;
        await this.lazyLoadingManager.requestFullInstanceLoadForCustomPath(createAbsolute(mappedPath));
        return mappedPath;
    }

    private splitToPathTokens(path: string): string[] {
        const tokens = [];
        let openBracketCount = 0;
        let currentToken = "";
        let isPrevBackSlash = false;
        for (const currentChar of path) {
            if (currentChar != undefined) {
                if (currentChar === "/" && openBracketCount === 0 && !isPrevBackSlash) {
                    tokens.push(currentToken);
                    currentToken = "";
                } else {
                    if (currentChar === "[") {
                        openBracketCount++;
                    }
                    if (currentChar === "]") {
                        openBracketCount--;
                    }
                    if (currentChar === "\\") {
                        isPrevBackSlash = true;
                    } else {
                        if (isPrevBackSlash && currentChar !== "/") {
                            currentToken += "\\";
                        }
                        currentToken = currentToken + currentChar;
                        isPrevBackSlash = false;
                    }
                }
            }
        }
        tokens.push(currentToken);
        return tokens.filter(isNotNullOrEmpty);
    }

    private getTextsBetweenBrackets(str: string | number): string[] {
        const allWordsBetweenBracketsRegex = /\[([^\]]+)\]/g;

        const results: string[] = [];

        while (true) {
            const nextMatch = allWordsBetweenBracketsRegex.exec(str.toString());
            if (nextMatch == undefined || nextMatch[1] == undefined) {
                break;
            }
            results.push(nextMatch[1]);
        }

        return results;
    }

    private removeLastInnerTextToken(tokens: string[]): string[] {
        if (tokens[tokens.length - 1] === "innerText") {
            return tokens.slice(0, -1);
        }
        return tokens;
    }

    private isInstanceIndex(filterOrIndex: string): boolean {
        return /^\d+$/.test(filterOrIndex);
    }

    private parseInstanceIndex(filterOrIndex: string): number {
        return Number(filterOrIndex);
    }

    private extractFilters(filterOrIndex: string): [PathFilter[], string] {
        const filters = this.getTextsBetweenBrackets(filterOrIndex);
        if (!arrayHasAtLeastOneItem(filters)) {
            throw new Error(`'${filterOrIndex}' must be expression in brackets`);
        }
        const lastFilter = IterUtils.last(filters);
        let index = "1";
        if (!this.isPathFilterExpression(lastFilter)) {
            index = lastFilter;
            filters.pop();
        }
        return [filters.map(x => this.createPathFilter(x)), index];
    }

    private isPathFilterExpression(pathFilterOrIndex: string): boolean {
        return pathFilterOrIndex.includes("=");
    }

    private createPathFilter(pathFilterExpression: string): PathFilter {
        const [path, value] = pathFilterExpression.split("=");
        if (path == undefined || value == undefined) {
            throw new Error(`'${pathFilterExpression}' must be a path filter expression`);
        }
        const patchedpath = this.simplePathMap(path);
        return new PathFilter(patchedpath, value);
    }

    private simplePathMap(path: string): string {
        return this.removeLastInnerTextToken(this.splitToPathTokens(path))
            .filter(t => t !== "1")
            .join("/");
    }

    private *filter(currentPath: string, instanceFilters: PathFilter[]): Iterable<string> {
        const instances = this.modelAccessor.getChildren(currentPath) ?? [];

        for (const instance of instances) {
            const instancePath = PathUtils.join([currentPath, instance]);
            if (instanceFilters.every(filter => filter.execute(this.modelAccessor, instancePath))) {
                yield instance;
            }
        }
    }

    private elementAt<T>(items: Iterable<T>, index: number): undefined | T {
        let currentIndex = 0;
        for (const item of items) {
            if (currentIndex === index) {
                return item;
            }
            currentIndex++;
        }
        return undefined;
    }

    private findIndexMatchTo(
        currentPath: string,
        instanceFilters: PathFilter[],
        highlanderTargetIndex: string
    ): string {
        const filteredInstances = this.filter(currentPath, instanceFilters);
        return this.elementAt(filteredInstances, this.parseInstanceIndex(highlanderTargetIndex) - 1) ?? "0";
    }
}

class PathFilter {
    private readonly path: string;
    private readonly value: string;

    public constructor(path: string, value: string) {
        this.path = path;
        this.value = value;
    }

    public execute(modelAccessor: IFormReadonlyModelAccessor, instancePath: string): boolean {
        const path = instancePath + "/" + this.path;
        return modelAccessor.getValue(path) === this.value;
    }
}
